import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Row, Col, Spinner, Modal, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import {
  getShopByName,
  convertUrlToShopName,
  getOrderStatus,
  updateOrderPaymentStatus
} from '../../services/shopService';
import Toast from '../../components/Toast';

const CNY_CACHE_KEY = 'hop_cny_rate';
const CNY_CACHE_TS_KEY = 'hop_cny_rate_ts';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 ore

const getCachedCnyRate = () => {
  try {
    const ts = localStorage.getItem(CNY_CACHE_TS_KEY);
    const rate = localStorage.getItem(CNY_CACHE_KEY);
    if (ts && rate && (Date.now() - parseInt(ts, 10)) < CACHE_TTL_MS) {
      return parseFloat(rate);
    }
  } catch (e) { /* ignore */ }
  return null;
};

const setCachedCnyRate = (rate) => {
  try {
    localStorage.setItem(CNY_CACHE_KEY, rate.toString());
    localStorage.setItem(CNY_CACHE_TS_KEY, Date.now().toString());
  } catch (e) { /* ignore */ }
};

const fetchCnyRate = async () => {
  try {
    const res = await fetch('https://api.frankfurter.app/latest?from=EUR&to=CNY');
    if (!res.ok) return null;
    const data = await res.json();
    const rate = data.rates?.CNY;
    if (rate) {
      setCachedCnyRate(rate);
      return rate;
    }
  } catch (e) {
    console.warn('Errore nel fetch del tasso CNY:', e);
  }
  return null;
};

const PaymentPage = () => {
  const { shopName, orderId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { cartItems, removeFromCart } = useCart();

  const [shopData, setShopData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('info');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [showWechatModal, setShowWechatModal] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [copied, setCopied] = useState(false);
  const [autoCnyRate, setAutoCnyRate] = useState(null);

  useEffect(() => {
    // Carica il tasso CNY: prima prova cache, poi fetch automatico
    const cached = getCachedCnyRate();
    if (cached) {
      setAutoCnyRate(cached);
    }
    fetchCnyRate().then(rate => {
      if (rate) setAutoCnyRate(rate);
    });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const convertedName = convertUrlToShopName(shopName);
        const shop = await getShopByName(convertedName);
        if (!shop) {
          setError('Negozio non trovato');
          return;
        }
        setShopData(shop);

        const order = await getOrderStatus(shop.id, orderId);
        if (!order) {
          setError('Ordine non trovato');
          return;
        }
        setOrderData(order);
      } catch (err) {
        console.error('Errore nel caricamento dei dati:', err);
        setError('Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [shopName, orderId]);

  const handlePayment = (method) => {
    setSelectedMethod(method);
    if (method === 'WeChat Pay') {
      setShowWechatModal(true);
    } else {
      setToastMessage(`${t('payment.coming_soon')}: ${method}`);
      setToastVariant('info');
      setShowToast(true);
    }
  };

  const handleCopyWechatId = async () => {
    try {
      await navigator.clipboard.writeText(wechatId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback per browser che non supportano clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = wechatId;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCodPayment = async () => {
    if (!shopData || !orderData) return;
    setConfirmingPayment(true);
    try {
      await updateOrderPaymentStatus(shopData.id, orderData.id || orderId, 'pending', 'Contrassegno');
      // Rimuovi gli item del negozio dal carrello
      const shopCartItems = cartItems.filter(item => item.shopId === shopData.id);
      shopCartItems.forEach(item => removeFromCart(item.id));
      setToastMessage('Ordine confermato con pagamento alla consegna.');
      setToastVariant('success');
      setShowToast(true);
      setTimeout(() => navigate('/profile'), 2000);
    } catch (err) {
      console.error('Errore pagamento alla consegna:', err);
      setToastMessage('Errore nella conferma dell\'ordine');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!shopData || !orderData) return;
    setConfirmingPayment(true);
    try {
      await updateOrderPaymentStatus(shopData.id, orderData.id || orderId, 'pending', 'WeChat Pay');
      // Rimuovi gli item del negozio dal carrello
      const shopCartItems = cartItems.filter(item => item.shopId === shopData.id);
      shopCartItems.forEach(item => removeFromCart(item.id));
      setShowWechatModal(false);
      setToastMessage('Pagamento segnalato. Il venditore verificherà e confermerà l\'ordine.');
      setToastVariant('success');
      setShowToast(true);
      setTimeout(() => navigate('/profile'), 2000);
    } catch (err) {
      console.error('Errore conferma pagamento:', err);
      setToastMessage('Errore nella conferma del pagamento');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setConfirmingPayment(false);
    }
  };

  const wechatId = shopData?.payments?.wechatPaymentId || shopData?.contact?.wechatId || '';
  const cnyRate = autoCnyRate || parseFloat(shopData?.payments?.cnyRate) || 7.80;
  const totalCny = orderData ? (orderData.total * cnyRate).toFixed(2) : '0.00';

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error || !orderData || !shopData) {
    return (
      <div className="text-center py-5 text-danger">
        {error || 'Dati non disponibili'}
      </div>
    );
  }

  return (
    <div className="py-2">
      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      {/* Tasto indietro */}
      <div className="mb-3 d-flex justify-content-start">
        <Button
          variant="dark"
          className="rounded-circle shadow-sm border-0"
          style={{ width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => navigate(`/checkout/${shopName}`)}
        >
          <i className="bi bi-arrow-left fs-4 text-white"></i>
        </Button>
      </div>

      <h4 className="mb-4 fw-bold">{t('payment.title')}</h4>

      {/* Riepilogo Ordine */}
      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          <h5 className="fw-bold mb-3">{t('payment.order_summary')}</h5>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">{t('checkout.shop') || 'Negozio'}</span>
            <span className="text-dark fw-medium">{shopData.name}</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">{t('payment.order_number')}</span>
            <span className="text-dark fw-medium">#{String(orderData.orderNumber).padStart(6, '0')}</span>
          </div>
          <hr />
          {orderData.items?.map((item, idx) => (
            <div key={idx} className="d-flex justify-content-between mb-1">
              <span className="text-secondary small">{item.name} x{item.quantity}</span>
              <span className="text-dark small">€{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <hr />
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">{t('checkout.subtotal')}</span>
            <span className="text-dark">€{(orderData.subtotal || orderData.total - (orderData.shippingCost || 0)).toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">{t('checkout.shipping')}</span>
            <span className="text-dark">€{(orderData.shippingCost || 0).toFixed(2)}</span>
          </div>
          <hr />
          <div className="d-flex justify-content-between">
            <span className="fw-bold text-dark fs-5">{t('checkout.total')}</span>
            <span className="fw-bold text-dark fs-5">€{orderData.total.toFixed(2)}</span>
          </div>
        </Card.Body>
      </Card>

      {/* Metodi di Pagamento */}
      <h5 className="mb-3 fw-bold">{t('payment.select_method')}</h5>

      {shopData?.payments?.paypal && (
        <button
          className="payment-btn paypal-btn mb-3 w-100"
          onClick={() => handlePayment('PayPal')}
        style={{
          backgroundColor: '#FFC439',
          border: 'none',
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}
      >
        <i className="bi bi-paypal" style={{ fontSize: '24px', color: '#003087' }}></i>
        <span style={{ fontSize: '18px', fontWeight: '700', color: '#003087' }}>PayPal</span>
      </button>
      )}

      {shopData?.payments?.wechatPay && (
        <button
          className="payment-btn wechat-btn mb-3 w-100"
          onClick={() => handlePayment('WeChat Pay')}
        style={{
          backgroundColor: '#07C160',
          border: 'none',
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        }}
      >
        <i className="bi bi-wechat" style={{ fontSize: '24px', color: '#fff' }}></i>
        <span style={{ fontSize: '18px', fontWeight: '700', color: '#fff' }}>WeChat Pay</span>
      </button>
      )}

      {shopData?.payments?.cashOnDelivery && (
        <button
          className="payment-btn cod-btn mb-3 w-100"
          onClick={handleCodPayment}
          disabled={confirmingPayment}
          style={{
            backgroundColor: '#6C757D',
            border: 'none',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          {confirmingPayment ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <i className="bi bi-cash-stack" style={{ fontSize: '24px', color: '#fff' }}></i>
          )}
          <span style={{ fontSize: '18px', fontWeight: '700', color: '#fff' }}>Pagamento alla consegna</span>
        </button>
      )}

      {!shopData?.payments?.paypal && !shopData?.payments?.wechatPay && !shopData?.payments?.cashOnDelivery && (
        <p className="text-muted text-center">Nessun metodo di pagamento disponibile per questo negozio.</p>
      )}

      <p className="text-muted small text-center mt-3">{t('payment.secure_notice')}</p>

      {/* Modale WeChat Pay */}
      <Modal
        show={showWechatModal}
        onHide={() => setShowWechatModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <i className="bi bi-wechat" style={{ color: '#07C160', fontSize: '28px' }}></i>
            {t('payment.wechat_pay_title')}
            <small className="text-muted d-block fw-normal" style={{ fontSize: '13px' }}>Pagamento manuale</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {/* QR Code */}
          {shopData?.payments?.wechatQrUrl ? (
            <div className="text-center mb-4">
              <p className="text-muted small mb-2">{t('payment.wechat_qr')}</p>
              <Image
                src={shopData.payments.wechatQrUrl}
                alt="WeChat Pay QR"
                style={{ maxWidth: 200, maxHeight: 200, borderRadius: 12 }}
              />
            </div>
          ) : (
            <div className="text-center mb-4 p-4 rounded-3" style={{ backgroundColor: '#F5F5F5' }}>
              <i className="bi bi-qr-code" style={{ fontSize: 64, color: '#ccc' }}></i>
              <p className="text-muted small mt-2 mb-0">QR code non disponibile</p>
            </div>
          )}

          {/* ID WeChat con tasto copia */}
          <div className="d-flex align-items-center justify-content-between mb-3 p-3 rounded-3" style={{ backgroundColor: '#F5F5F5' }}>
            <div>
              <small className="text-muted d-block">{t('payment.wechat_id_label')}</small>
              <span className="fw-bold text-dark">{wechatId || 'Non impostato'}</span>
            </div>
            {wechatId && (
              <Button
                variant={copied ? 'success' : 'outline-secondary'}
                size="sm"
                className="rounded-pill d-flex align-items-center gap-1"
                onClick={handleCopyWechatId}
                style={{ minWidth: 100, justifyContent: 'center' }}
              >
                {copied ? (
                  <>
                    <i className="bi bi-check-lg"></i>
                    {t('payment.copied')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-clipboard"></i>
                    Copia
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Totali EUR e CNY */}
          <div className="mb-4 p-3 rounded-3" style={{ backgroundColor: '#F5F5F5' }}>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-secondary">Totale EUR</span>
              <span className="fw-bold text-dark fs-5">€{orderData.total.toFixed(2)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-secondary">{t('payment.total_cny')}</span>
              <span className="fw-bold text-dark fs-5">¥{totalCny}</span>
            </div>
            <small className="text-muted">
              Tasso: 1 EUR = {cnyRate} CNY
              {autoCnyRate ? ' (aggiornato automaticamente)' : ''}
            </small>
          </div>

          {/* Bottoni azione */}
          <Row className="g-2">
            <Col xs={6}>
              <a
                href="weixin://"
                className="btn w-100 rounded-pill d-flex align-items-center justify-content-center gap-2"
                style={{
                  backgroundColor: '#07C160',
                  color: '#fff',
                  border: 'none',
                  padding: '12px',
                  fontWeight: 600,
                  textDecoration: 'none'
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-wechat"></i>
                {t('payment.open_wechat')}
              </a>
            </Col>
            <Col xs={6}>
              <Button
                variant="dark"
                className="w-100 rounded-pill d-flex align-items-center justify-content-center gap-2"
                style={{ padding: '12px', fontWeight: 600 }}
                onClick={handleConfirmPayment}
                disabled={confirmingPayment}
              >
                {confirmingPayment ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="bi bi-check-lg"></i>
                )}
                {t('payment.confirm_payment')}
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PaymentPage;
